import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { getIsWorkflowPhased } from '@aline/common';
import { params } from '@aline/routes';
import { IconButton, Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import {
  Ban,
  CircleCheck,
  ListTodo,
  MessageSquareWarning,
  Send,
  TriangleAlert,
  Undo2,
} from 'lucide-react';
import { AlineButton, AlineTooltip } from 'src/components';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  getAssigneeDisplayDetails,
  getPermissionAssigneeDisplayDetails,
  useConsumePendingParam,
} from 'src/helpers';
import { useChangeTaskStatus } from 'src/hooks';
import { getIsSigningPhaseType } from 'src/workflow/helpers';
import { makeStyles } from 'tss-react/mui';

import { FOOTER_ACTION_ICON } from './footer-action-icon';
import { getReReviewReviewers, RequestReReviewButton } from './RequestReReviewButton';
import type { FooterAction, ReviewAssignment, YourNextStep } from './task-review-helpers';
import {
  FOOTER_ACTION,
  FOOTER_ACTION_LABEL,
  formatDay,
  getActionForDecisionParam,
  getApproveBlockedReason,
  getFooterActionBlockedReason,
  getFooterActions,
  getFooterCompleteBlockedReason,
  getFooterCompleteWarning,
  getHasOpenReviewRequest,
  getHasReviewBegun,
  getHasReviewers,
  getIsReviewerRow,
  getIsReviewPending,
  getNotifiedText,
  getPrimaryFooterAction,
  getReadyToCompleteText,
  getRequesterAssignment,
  getReviewReminderMessage,
  getReviewRequestText,
  getTaskIsOpen,
  getUploadedFilesNotice,
  getYouFinishedText,
  getYouRequestedChangesText,
} from './task-review-helpers';
import { TaskCommentDialog } from './TaskCommentDialog';
import type { PillTone } from './TaskStatusPill';
import { TONE } from './TaskStatusPill';
import { ReopenTaskDialog, useReopenTask } from './use-reopen-task';
import { StartWorkflowAfterTaskDialog, useStartTask } from './use-start-task';
import { useTaskReviewActions } from './use-task-review-actions';
import { useUnsubmittedReview } from './use-unsubmitted-review';
import {
  ParkedWorkflowCompletionDialog,
  useParkedWorkflowCompletion,
} from '../../use-parked-workflow-completion';
import type { ParkedWorkflowMeta } from '../../use-parked-workflow-completion';

const DONE_GREEN = '#12B066';

const useStyles = makeStyles()(theme => ({
  // As tall as the buttons, so swapping them for the done line doesn't shift the panel.
  actions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: 32,
  },
  // The task page's action bar, pinned under the page's scroller.
  bar: {
    alignItems: 'center',
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    borderTop: `1px solid ${theme.appColors.ruleSubtle}`,
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    minHeight: 64,
    padding: theme.spacing(1.5, 3),
  },
  barActions: {
    gap: theme.spacing(1.5),
  },
  barDetail: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    lineHeight: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  barDetailIcon: {
    marginRight: theme.spacing(0.5),
    verticalAlign: -2,
  },
  barLead: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  barText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  barTile: {
    alignItems: 'center',
    border: '1px solid',
    borderRadius: 8,
    display: 'flex',
    flexShrink: 0,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  barTitle: {
    color: theme.appColors.strongType,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  byline: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    fontSize: 12,
    gap: theme.spacing(0.75),
  },
  bylineIcon: {
    flexShrink: 0,
  },
  done: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
    minWidth: 0,
  },
  doneIcon: {
    flexShrink: 0,
  },
  doneTitle: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footer: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 2),
  },
  grow: {
    flex: 1,
  },
  status: {
    color: theme.appColors.iconsType,
    fontSize: 14,
  },
}));

type FooterTask = Pick<
  Apollo.TaskReviewFieldsFragment,
  | 'assignments'
  | 'counterpartyName'
  | 'hasExternalAssignees'
  | 'id'
  | 'isDelegatedToAi'
  | 'ownerName'
  | 'rejectedAt'
  | 'rejectedBy'
  | 'requesterIsInternal'
  | 'status'
> &
  Pick<Apollo.TaskViewerActionFieldsFragment, 'viewerActionRecipients'> & {
    completedAt: string | null;
    completedBy: { displayName: string } | null;
    phase: { completedAt: string | null; startedAt: string | null };
    requesterDisallowedReason: { changeStatus?: string | null; edit?: string | null };
    startedAt: string | null;
  };

type FooterWorkflow = ParkedWorkflowMeta & {
  boardId: UUID;
  phases: readonly { id: UUID; __typename?: 'NonSigningPhase' | 'SigningPhase' }[];
};

const NEXT_STEP_NOTE = {
  info: { icon: ListTodo, tone: TONE.blue },
  success: { icon: CircleCheck, tone: TONE.green },
} satisfies Record<YourNextStep['severity'], Pick<FooterNote, 'icon' | 'tone'>>;

/** A line about where the viewer stands, e.g. a notice or who was asked to review. */
interface FooterNote {
  icon: LucideIcon;
  text: string;
  tone: PillTone;
  detail?: string;
}

/** The dialogs a footer action opens; Start and Mark complete run without one. */
type FooterDialog = Exclude<
  FooterAction,
  typeof FOOTER_ACTION.MarkComplete | typeof FOOTER_ACTION.Start
>;

interface FinishDialogCopy {
  label: string;
  placeholder: string;
  title: string;
}

/** Complete and Approve record the viewer's part through the same status change. */
const FINISH_DIALOG_COPY = {
  [FOOTER_ACTION.Approve]: { label: 'Comment', placeholder: 'Type a comment...', title: 'Approve' },
  [FOOTER_ACTION.Complete]: {
    label: 'Comment',
    placeholder: 'Type a comment...',
    title: 'Complete task',
  },
} satisfies Record<string, FinishDialogCopy>;

interface DoneSummary {
  title: string;
  undoLabel: string;
}

interface TaskReviewFooterProps {
  readOnly: boolean;
  task: FooterTask;
  workflow: FooterWorkflow;
  /** Preview as external shows the external's actions, but nothing runs. */
  isPreview?: boolean;
  /** The bar's headline on the left: the viewer's next step. */
  lead?: YourNextStep | null;
  /**
   * `panel` stacks the lines over full-width buttons under the side panel. `bar` is the task
   * page's full-width bar, with the lines on the left and the buttons on the right.
   */
  variant?: 'bar' | 'panel';
}

/**
 * The viewer's part in the task, from their role and whose turn it is. Once they have made their
 * decision, or the task is complete, it shows that with an undo instead. An assignee asks for
 * review, then completes once the required reviewers approve. After changes are requested,
 * Request Re-Review sits beside Complete; reminding or withdrawing one reviewer happens on that
 * reviewer's row. Redlines upload from the document cards.
 */
export function TaskReviewFooter({
  isPreview = false,
  lead = null,
  readOnly,
  task,
  variant = 'panel',
  workflow,
}: TaskReviewFooterProps): JSX.Element | null {
  const { classes, cx } = useStyles();
  const isBar = variant === 'bar';
  const reopen = useReopenTask(task.id);
  const starter = useStartTask(workflow);
  const currentUserId = useCurrentUserId();
  const actions = useTaskReviewActions(task.id);
  const pendingCommentRef = useRef<string | null>(null);
  const [dialog, setDialog] = useState<FooterDialog | null>(null);
  const { loading: changingStatus, markComplete } = useChangeTaskStatus(task.id);
  const onComplete = useCallback(
    (options: { confirmUnpark: boolean }) =>
      markComplete({ ...options, comment: pendingCommentRef.current }),
    [markComplete]
  );
  const { completeTask, confirmAndComplete, dismissPrompt, prompt } = useParkedWorkflowCompletion({
    canManageWorkflow: task.requesterIsInternal && !isPreview,
    onComplete,
    workflow,
  });
  // The server works out who the viewer's next action reaches, the same way it sends the email.
  const notifiedText = isPreview
    ? null
    : getNotifiedText(
        task.viewerActionRecipients.map(user => getAssigneeDisplayDetails(user).primary)
      );

  // A preview stands in for the first external on the task.
  const viewer = isPreview
    ? (task.assignments.find(assignment => assignment.isExternal) ?? null)
    : getRequesterAssignment(task.assignments, currentUserId);
  const isOpen = getTaskIsOpen(task);
  const isExternalView = isPreview || !task.requesterIsInternal;
  const canEdit = !task.requesterDisallowedReason.edit && !isExternalView;
  const footerActions = readOnly
    ? []
    : getFooterActions({
        canEdit,
        isInternal: !isExternalView,
        isOpen,
        isPhaseActive: Boolean(task.phase.startedAt) && !task.phase.completedAt,
        isPhased: getIsWorkflowPhased(workflow.phases, getIsSigningPhaseType),
        isStarted: Boolean(task.startedAt),
        task,
        viewer,
      });
  const done = getDoneSummary({ task, viewer });
  const uploadedNotice = readOnly
    ? null
    : getUploadedFilesNotice({
        assignment: viewer,
        hasReviewers: getHasReviewers(task.assignments),
        ownerName: (!isExternalView && viewer?.reviewRequestedBy?.displayName) || task.ownerName,
        status: task.status,
      });
  const isAssigneeViewer = isOpen && viewer?.role === Apollo.TaskAssigneeRole.Assignee;
  const reviewRequestText =
    isAssigneeViewer && getHasOpenReviewRequest(task.assignments)
      ? getReviewRequestText(task.assignments, { isExternalView, viewerUserId: currentUserId })
      : null;
  const completeBlockedReason = footerActions.includes(FOOTER_ACTION.Complete)
    ? getFooterCompleteBlockedReason({
        assignments: task.assignments,
        canEdit,
        isExternalView,
        viewer,
      })
    : null;
  const readyToCompleteText =
    isAssigneeViewer && getHasReviewBegun(task.assignments)
      ? getReadyToCompleteText(task.assignments, { isExternalView })
      : null;
  const completeWarning = getFooterCompleteWarning({
    assignments: task.assignments,
    isExternalView,
    viewer,
  });
  const canApprove = footerActions.includes(FOOTER_ACTION.Approve);
  const { review } = useUnsubmittedReview(task.id, { skip: isPreview || !canApprove });
  const approveBlockedReason = canApprove ? getApproveBlockedReason(review) : null;
  const changeStatusDisabledReason = task.requesterDisallowedReason.changeStatus ?? null;
  const isDisabled = isPreview || Boolean(changeStatusDisabledReason);
  const disabledReason = isPreview ? 'Preview only' : changeStatusDisabledReason;
  const reReviewReviewers = useMemo(
    () => (isAssigneeViewer ? getReReviewReviewers(task.assignments) : []),
    [isAssigneeViewer, task.assignments]
  );
  const reReviewButton = reReviewReviewers.length > 0 && (
    <RequestReReviewButton
      disabledReason={disabledReason}
      isBar={isBar}
      isPrimary={Boolean(completeBlockedReason)}
      reviewers={reReviewReviewers}
      taskId={task.id}
    />
  );

  const closeDialog = useCallback(() => setDialog(null), []);
  const [decisionArrival, acknowledgeDecision] = useConsumePendingParam(params.taskDecision.key);
  const decisionDialog = getDialogForAction(
    getActionForDecisionParam(decisionArrival?.value ?? null, footerActions)
  );

  const handleAction = useCallback(
    (action: FooterAction) => {
      // Complete only asks first when it has a warning to show.
      const completesNow =
        action === FOOTER_ACTION.MarkComplete ||
        (action === FOOTER_ACTION.Complete && !completeWarning);
      if (action === FOOTER_ACTION.Start) void starter.requestStart(task.id);
      else if (completesNow) {
        pendingCommentRef.current = null;
        completeTask();
      } else setDialog(action);
    },
    [completeTask, completeWarning, starter, task.id]
  );
  const finish = useCallback(
    async (comment: string) => {
      pendingCommentRef.current = comment || null;
      completeTask();
      return true;
    },
    [completeTask]
  );

  useEffect(() => {
    if (!decisionArrival) return;
    acknowledgeDecision();
    if (decisionDialog) setDialog(decisionDialog);
  }, [acknowledgeDecision, decisionArrival, decisionDialog]);

  if (task.status === Apollo.TaskStatus.Template) return null;
  if (task.rejectedAt) {
    const rejectedText = `Rejected${task.rejectedBy ? ` by ${task.rejectedBy.displayName}` : ''}`;
    const reopenText = 'Move the workflow out of Cancelled to reopen it.';
    if (isBar)
      return (
        <div className={classes.bar}>
          <FooterBarLead
            notes={[{ detail: reopenText, icon: Ban, text: rejectedText, tone: TONE.red }]}
          />
        </div>
      );
    return (
      <div className={classes.footer}>
        <Typography className={classes.status}>{`${rejectedText}. ${reopenText}`}</Typography>
      </div>
    );
  }
  // The task page bar is where the next step is said, and that can be the only line.
  if (!done && !footerActions.length && !(isBar && lead)) return null;

  const finishCopy = getFinishDialogCopy(dialog);
  const viewerRequestedChanges =
    isOpen && viewer?.decision === Apollo.TaskAssigneeDecision.ChangesRequested;
  const notes: FooterNote[] = [];
  if (isBar && done) notes.push({ icon: CircleCheck, text: done.title, tone: TONE.green });
  else if (isBar && lead)
    notes.push({ ...NEXT_STEP_NOTE[lead.severity], detail: lead.detail, text: lead.title });
  if (uploadedNotice) notes.push({ icon: TriangleAlert, text: uploadedNotice, tone: TONE.orange });
  // The bar's next step already says the viewer requested changes.
  if (viewerRequestedChanges && !(isBar && lead))
    notes.push({
      icon: MessageSquareWarning,
      text: getYouRequestedChangesText(viewer?.decidedAt ?? null),
      tone: TONE.orange,
    });
  if (reviewRequestText) notes.push({ icon: Send, text: reviewRequestText, tone: TONE.blue });
  if (readyToCompleteText)
    notes.push({ icon: CircleCheck, text: readyToCompleteText, tone: TONE.green });

  const bylines = notes.map(({ icon: Icon, text, tone }) => (
    <Typography key={text} className={classes.byline}>
      <Icon className={classes.bylineIcon} color={tone.text} size={14} />
      {text}
    </Typography>
  ));
  const actionRow = (
    <div className={cx(classes.actions, isBar && classes.barActions)}>
      <FooterPrimaryActions
        actions={footerActions}
        approveBlockedReason={approveBlockedReason}
        completeBlockedReason={completeBlockedReason}
        disabled={isDisabled}
        disabledReason={disabledReason}
        done={done}
        isBar={isBar}
        loading={changingStatus || actions.loading || starter.loading}
        reReviewButton={reReviewButton}
        onAction={handleAction}
      />
      {!done && footerActions.includes(FOOTER_ACTION.Reject) && (
        <AlineTooltip arrow placement="top" title={changeStatusDisabledReason ?? 'Reject task'}>
          <span>
            <IconButton
              aria-label="Reject task"
              disabled={isDisabled}
              size="small"
              onClick={() => setDialog(FOOTER_ACTION.Reject)}
            >
              <Ban size={18} />
            </IconButton>
          </span>
        </AlineTooltip>
      )}
      {done && !readOnly && !isPreview && (
        <AlineTooltip arrow placement="top" title={changeStatusDisabledReason ?? done.undoLabel}>
          <span>
            <IconButton
              aria-label={done.undoLabel}
              disabled={Boolean(changeStatusDisabledReason) || changingStatus}
              size="small"
              onClick={reopen.requestReopen}
            >
              <Undo2 size={18} />
            </IconButton>
          </span>
        </AlineTooltip>
      )}
    </div>
  );
  const dialogs = (
    <>
      <TaskCommentDialog
        label={finishCopy?.label ?? ''}
        notifiedText={notifiedText}
        open={Boolean(finishCopy)}
        placeholder={finishCopy?.placeholder ?? ''}
        primaryText={dialog ? FOOTER_ACTION_LABEL[dialog] : ''}
        title={finishCopy?.title ?? ''}
        warning={dialog === FOOTER_ACTION.Complete ? completeWarning : null}
        onClose={closeDialog}
        onSubmit={finish}
      />
      <TaskCommentDialog
        label="Note"
        notifiedText={notifiedText}
        open={dialog === FOOTER_ACTION.RequestReview}
        placeholder="Type a message for the reviewers..."
        primaryText={FOOTER_ACTION_LABEL[FOOTER_ACTION.RequestReview]}
        title="Request review"
        onClose={closeDialog}
        onSubmit={note => actions.requestReview({ note })}
      />
      <TaskCommentDialog
        isCommentRequired
        label="What should change?"
        message={viewer ? undefined : "You'll be added to this task as an optional reviewer."}
        notifiedText={notifiedText}
        open={dialog === FOOTER_ACTION.RequestChanges}
        placeholder="Type your requested changes..."
        primaryText={FOOTER_ACTION_LABEL[FOOTER_ACTION.RequestChanges]}
        title="Submit changes"
        onClose={closeDialog}
        onSubmit={comment => actions.requestChanges(comment)}
      />
      <TaskCommentDialog
        label="Note"
        notifiedText={notifiedText}
        open={dialog === FOOTER_ACTION.Remind}
        placeholder="Type a note about your requested changes..."
        primaryText="Send Reminder"
        title="Send a reminder"
        onClose={closeDialog}
        onSubmit={note => actions.remind(note)}
      />
      <TaskCommentDialog
        label="Note"
        message={getReviewReminderMessage(
          task.assignments
            .filter(getIsReviewPending)
            .map(assignment => getPermissionAssigneeDisplayDetails(assignment.permission).primary)
        )}
        open={dialog === FOOTER_ACTION.RemindReviewers}
        placeholder="Type a note for the reviewers..."
        primaryText="Send Reminder"
        title="Remind reviewers"
        onClose={closeDialog}
        onSubmit={note => actions.remindReviewers(note)}
      />
      <TaskCommentDialog
        isCommentRequired
        label="Why are you rejecting this task?"
        message="This will mark the task rejected and move the workflow to Cancelled. Continue?"
        open={dialog === FOOTER_ACTION.Reject}
        placeholder="Type a reason..."
        primaryText={FOOTER_ACTION_LABEL[FOOTER_ACTION.Reject]}
        title="Reject task"
        onClose={closeDialog}
        onSubmit={reason => actions.reject(reason)}
      />
      <ParkedWorkflowCompletionDialog
        prompt={prompt}
        onClose={dismissPrompt}
        onConfirm={confirmAndComplete}
      />
      <StartWorkflowAfterTaskDialog
        open={starter.isPromptOpen}
        workflow={workflow}
        onClose={starter.dismissPrompt}
      />
      <ReopenTaskDialog
        nextRound={reopen.nextRound}
        open={reopen.isPromptOpen}
        onClose={reopen.dismissPrompt}
        onConfirm={reopen.confirmReopen}
      />
    </>
  );

  if (isBar)
    return (
      <div className={classes.bar}>
        <FooterBarLead notes={notes} />
        {actionRow}
        {dialogs}
      </div>
    );

  return (
    <div className={classes.footer}>
      {bylines}
      {actionRow}
      {dialogs}
    </div>
  );
}

interface FooterPrimaryActionsProps {
  actions: readonly FooterAction[];
  // Why Approve waits: the reviewer's own markup this round isn't submitted.
  approveBlockedReason: string | null;
  // Why Complete waits: a required reviewer hasn't approved.
  completeBlockedReason: string | null;
  disabled: boolean;
  disabledReason: string | null;
  done: DoneSummary | null;
  // The page bar sizes buttons to their labels; the panel stretches them across its width.
  isBar: boolean;
  loading: boolean;
  onAction: (action: FooterAction) => void;
  /** Sits left of Complete after changes were requested, and takes the fill while Complete waits. */
  reReviewButton: React.ReactNode;
}

/**
 * The viewer's decision once made, or their buttons: the first action filled and on the right,
 * the rest outlined to its left.
 */
function FooterPrimaryActions({
  actions,
  approveBlockedReason,
  completeBlockedReason,
  disabled,
  disabledReason,
  done,
  isBar,
  loading,
  onAction,
  reReviewButton,
}: FooterPrimaryActionsProps): JSX.Element | null {
  const { classes } = useStyles();

  // The bar says it on the left instead.
  if (done && isBar) return null;
  if (done)
    return (
      <div className={classes.done}>
        <CircleCheck className={classes.doneIcon} color={DONE_GREEN} size={20} />
        <Typography className={classes.doneTitle}>{done.title}</Typography>
      </div>
    );

  const buttons = actions.filter(action => action !== FOOTER_ACTION.Reject);
  const filledAction = getPrimaryFooterAction({
    actions: buttons,
    hasReReview: Boolean(reReviewButton),
    isCompleteBlocked: Boolean(completeBlockedReason),
  });
  return (
    <>
      {buttons.toReversed().map(action => {
        const isFirst = action === buttons[0];
        const isComplete = action === FOOTER_ACTION.Complete;
        const blockedReason = getFooterActionBlockedReason(action, {
          approveBlockedReason,
          completeBlockedReason,
        });
        const isFilled = action === filledAction;
        const Icon = FOOTER_ACTION_ICON[action];
        return (
          <React.Fragment key={action}>
            {isComplete && reReviewButton}
            <AlineTooltip arrow placement="top" title={disabledReason ?? blockedReason ?? ''}>
              <div className={isBar ? undefined : classes.grow}>
                <AlineButton
                  color="primary"
                  disabled={disabled || Boolean(blockedReason)}
                  fullWidth={!isBar}
                  loading={isFirst && loading}
                  startIcon={<Icon size={16} />}
                  variant={isFilled ? 'contained' : 'outlined'}
                  onClick={() => onAction(action)}
                >
                  {FOOTER_ACTION_LABEL[action]}
                </AlineButton>
              </div>
            </AlineTooltip>
          </React.Fragment>
        );
      })}
    </>
  );
}

/** The headline note in a tinted tile, with its detail and the other notes muted beneath it. */
function FooterBarLead({ notes }: { notes: readonly FooterNote[] }): JSX.Element {
  const { classes } = useStyles();
  const [headline, ...others] = notes;
  if (!headline) return <div />;
  const { icon: Icon, tone } = headline;

  return (
    <div className={classes.barLead}>
      <span
        className={classes.barTile}
        style={{ backgroundColor: tone.background, borderColor: tone.border, color: tone.text }}
      >
        <Icon size={18} />
      </span>
      <div className={classes.barText}>
        <Typography className={classes.barTitle} title={headline.text}>
          {headline.text}
        </Typography>
        {Boolean(headline.detail) && (
          <Typography className={classes.barDetail} title={headline.detail}>
            {headline.detail}
          </Typography>
        )}
        {others.map(({ icon: OtherIcon, text, tone: otherTone }) => (
          <Typography key={text} className={classes.barDetail} title={text}>
            <OtherIcon className={classes.barDetailIcon} color={otherTone.text} size={12} />
            {text}
          </Typography>
        ))}
      </div>
    </div>
  );
}

function getDialogForAction(action: FooterAction | null): FooterDialog | null {
  if (!action || action === FOOTER_ACTION.Start || action === FOOTER_ACTION.MarkComplete)
    return null;
  return action;
}

function getFinishDialogCopy(dialog: FooterDialog | null): FinishDialogCopy | null {
  if (dialog === FOOTER_ACTION.Approve || dialog === FOOTER_ACTION.Complete)
    return FINISH_DIALOG_COPY[dialog];
  return null;
}

function getDoneSummary(options: {
  task: FooterTask;
  viewer: Pick<ReviewAssignment, 'decidedAt' | 'decision' | 'role'> | null;
}): DoneSummary | null {
  const { task, viewer } = options;
  if (task.completedAt)
    return {
      title: getCompletedText(task.completedBy, task.completedAt),
      undoLabel: 'Reopen task',
    };
  if (!getTaskIsOpen(task) || viewer?.decision !== Apollo.TaskAssigneeDecision.Completed)
    return null;
  const isReviewer = getIsReviewerRow(viewer);
  return {
    title: getYouFinishedText({ decidedAt: viewer.decidedAt, isReviewer }),
    undoLabel: isReviewer ? 'Undo your approval' : 'Undo your completion',
  };
}

/** "Completed by Brent Farese on Sep 27" */
function getCompletedText(
  completedBy: { displayName: string } | null,
  completedAt: string
): string {
  const by = completedBy ? ` by ${completedBy.displayName}` : '';
  return `Completed${by} on ${formatDay(completedAt)}`;
}
